import React, {useEffect, useMemo, useState} from "react";
import { Edit2, Plus, Save, Tag, Trash2, X } from "lucide-react";
import {useManageCategories} from "./useManageCategory";

// Local modal category type (simplified)
interface ModalCategory {
    id?: number;
    name: string;
}

interface ManageCategoriesModalProps {
    onClose: () => void;
    onUpdate: (categories: { service: ModalCategory[]; product: ModalCategory[] }) => void;
}

function ManageCategoriesModal({ onClose, onUpdate,categories }: any) {
    const {
        // categories,
        addCategory,
        editCategory,
        deleteCategory,
        saveChanges,
    } = useManageCategories(onUpdate);

    const [activeType, setActiveType] = useState<"service" | "product">("service");
    const [newCategory, setNewCategory] = useState("");
    const [editingCategory, setEditingCategory] = useState<ModalCategory | null>(null);
    const [editValue, setEditValue] = useState("");

    const handleAddCategory = () => {
        addCategory(activeType, newCategory);
        setNewCategory("");
    };

    const handleEditCategory = (oldCategory: ModalCategory, newName: string) => {
        editCategory(activeType, oldCategory, newName);
        setEditingCategory(null);
        setEditValue("");
    };

    const handleDeleteCategory = async (cat: ModalCategory) => {
       await deleteCategory(activeType, cat);
    };

    const activeCategories=useMemo(()=>categories[activeType],[activeType])

    const handleSave = () => {
        saveChanges();
        onClose();
    };



    const getCategoryColor = (type: "service" | "product") => {
        if (type === "service") {
            return "bg-blue-100 text-blue-800 border-blue-200";
        }
        return "bg-purple-100 text-purple-800 border-purple-200";
    };



    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto shadow-lg">
                {/* Header */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-t-lg p-4 border-b border-gray-100">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center shadow-sm">
                                <Tag className="w-4 h-4 text-white" />
                            </div>
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900">Manage Categories</h3>
                                <p className="text-xs text-gray-600">
                                    Add, edit, or remove service and product categories
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="p-6">
                    {/* Type Selector */}
                    <div className="flex items-center space-x-4 mb-6">
                        <span className="text-sm font-medium text-gray-700">Category Type:</span>
                        <div className="flex space-x-4">
                            <label className="flex items-center">
                                <input
                                    type="radio"
                                    name="categoryType"
                                    value="service"
                                    checked={activeType === "service"}
                                    onChange={(e) => setActiveType(e.target.value as "service" | "product")}
                                    className="mr-2 text-blue-600 focus:ring-blue-500"
                                />
                                <span className="text-sm text-gray-700">Services</span>
                            </label>
                            <label className="flex items-center">
                                <input
                                    type="radio"
                                    name="categoryType"
                                    value="product"
                                    checked={activeType === "product"}
                                    onChange={(e) => setActiveType(e.target.value as "service" | "product")}
                                    className="mr-2 text-blue-600 focus:ring-blue-500"
                                />
                                <span className="text-sm text-gray-700">Products</span>
                            </label>
                        </div>
                    </div>

                    {/* Add New Category */}
                    <div className="bg-gray-50 rounded-lg p-4 mb-6">
                        <h4 className="text-sm font-medium text-gray-900 mb-3">Add New Category</h4>
                        <div className="flex space-x-3">
                            <input
                                type="text"
                                value={newCategory}
                                onChange={(e) => setNewCategory(e.target.value)}
                                placeholder={`Enter new ${activeType} category`}
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                onKeyDown={(e) => e.key === "Enter" && handleAddCategory()}
                            />
                            <button
                                onClick={handleAddCategory}
                                disabled={!newCategory.trim()}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                            >
                                <Plus className="w-4 h-4" />
                            </button>
                        </div>
                    </div>

                    {/* Categories List */}
                    <div>
                        <h4 className="text-sm font-medium text-gray-900 mb-3">
                            {activeType === "service" ? "Service" : "Product"} Categories (
                            {categories[activeType].length})
                        </h4>
                        <div className="space-y-2 max-h-60 overflow-y-auto">
                            {categories[activeType].map((category:any) => {

                             return (
                                <div
                                    key={category.id}
                                    className={`flex items-center justify-between p-3 rounded-lg border ${getCategoryColor(
                                        activeType
                                    )}`}
                                >
                                    {editingCategory?.id === category.id ? (
                                        <div className="flex items-center space-x-2 flex-1">
                                            <input
                                                type="text"
                                                value={editValue}
                                                onChange={(e) => setEditValue(e.target.value)}
                                                className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                                onKeyDown={(e) => {
                                                    if (e.key === "Enter") handleEditCategory(category, editValue);
                                                    if (e.key === "Escape") {
                                                        setEditingCategory(null);
                                                        setEditValue("");
                                                    }
                                                }}
                                                autoFocus
                                            />
                                            <button
                                                onClick={() => handleEditCategory(category, editValue)}
                                                className="p-1 text-green-600 hover:text-green-700"
                                            >
                                                <Save className="w-4 h-4" />
                                            </button>
                                            <button
                                                onClick={() => {
                                                    setEditingCategory(null);
                                                    setEditValue("");
                                                }}
                                                className="p-1 text-gray-400 hover:text-gray-600"
                                            >
                                                <X className="w-4 h-4" />
                                            </button>
                                        </div>
                                    ) : (
                                        <>
                                            <span className="text-sm font-medium">{category.name}</span>
                                            <div className="flex items-center space-x-1">
                                                <button
                                                    onClick={() => {
                                                        setEditingCategory(category);
                                                        setEditValue(category.name);
                                                    }}
                                                    className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                                                >
                                                    <Edit2 className="w-4 h-4" />
                                                </button>
                                                <button
                                                    onClick={() => handleDeleteCategory(category)}
                                                    className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </>
                                    )}
                                </div>
                            )}
                            )}
                            {categories[activeType].length === 0 && (
                                <div className="text-center py-8 text-gray-500">
                                    <Tag className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                                    <p className="text-sm">No {activeType} categories yet</p>
                                    <p className="text-xs">Add your first category above</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="flex justify-end space-x-3 p-6 border-t border-gray-200 bg-gray-50 rounded-b-lg">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                        Cancel
                    </button>
                    {/*<button*/}
                    {/*    onClick={handleSave}*/}
                    {/*    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"*/}
                    {/*>*/}
                    {/*    Save Changes*/}
                    {/*</button>*/}
                </div>
            </div>
        </div>
    );
}

export default ManageCategoriesModal;
